import { BenefitLayout } from "providers/benefit-layout";
import React, { FC, useState } from "react";
import { useForm } from "shared/hooks/use-form";
import { DateInput } from "shared/components/forms/DateInput";
import { Button, ButtonVariant } from "shared/components/Button";
import api from "api";
import { View } from "../../../../types";
import { toast } from "react-toastify";
import { moveOutDateValidations } from "../../../../validations";

import { useNavigate, useParams } from "react-router-dom";
import { CustomerIssue } from "types/issue";
import { SuccessScreenView } from "../../../../components/SuccessScreen";

type Props = {
  setView: (view: View) => void;
  issue?: CustomerIssue;
};

enum Steps {
  MoveOut = "MoveOut",
  Success = "Success"
}

export const MovedOutScreenView: FC<Props> = ({ setView, issue }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { register, onSubmit } = useForm(
    {
      movedOutDate: ""
    },
    moveOutDateValidations(issue?.params?.min_moved_out_date)
  );
  const [step, setStep] = useState(Steps.MoveOut);
  const hasUnverifiedRvs = issue?.params?.has_unverified_rental_verifications;

  if (step === Steps.MoveOut)
    return (
      <BenefitLayout
        hasBack={true}
        title="When did you move out?"
        mainClassName="max-w-[440px] w-full mx-auto md:mt-4"
        footer={
          <div className="max-w-[440px] mx-auto w-full">
            <Button
              variant={ButtonVariant.PRIMARY}
              className="w-full"
              type="submit"
              form="edit-move-out-date-form"
            >
              Confirm date
            </Button>
            <Button
              className="w-full md:block hidden mt-2"
              onClick={() => setView(View.SelectView)}
            >
              Back
            </Button>
          </div>
        }
      >
        <div>
          <p className="text-sm text-content-600 mb-6">
            This should be the last date your lease was active, even if you
            physically moved out a few days prior.
          </p>
          <form
            id="edit-move-out-date-form"
            onSubmit={onSubmit({
              onSuccess: (data) => {
                api
                  .confirmMoveOut({
                    id: Number(id),
                    moved_out_date: data.movedOutDate
                  })
                  .then(() => setStep(Steps.Success));
              },
              onError: (e) => {
                toast.error(Object.values(e).join(", "));
              }
            })}
          >
            <DateInput
              id="move_out_date"
              label="Move out date"
              hideOptional
              {...register("movedOutDate")}
            />
          </form>
        </div>
      </BenefitLayout>
    );

  return (
    <SuccessScreenView
      title={hasUnverifiedRvs ? "We’ve updated your lease end date!" : "Great!"}
      subtext1={
        hasUnverifiedRvs
          ? "Please click below to verify your missing payments"
          : "You’re all set. We’ll close out your tradeline appropriately."
      }
      subtext2={
        hasUnverifiedRvs
          ? undefined
          : "You can add your next rental address for reporting from the home screen"
      }
      buttonTitle={hasUnverifiedRvs ? "Verify payments" : "Done"}
      onButtonClick={() => {
        const link = hasUnverifiedRvs
          ? "/rent_reporting/issues/verify-rent"
          : "/hub";
        navigate(link);
      }}
    />
  );
};
